<script setup lang="ts">
import { loadMyPropertyPreferences, myPropertyPreferences, saveMyPropertyPreferences } from '@/appLogic/view/preferences';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { openFailToSavedModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import ToggleSwitch from '@/corePagesModule/components/ToggleSwitch.vue';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { onBeforeMount, reactive } from 'vue';
import { useRouter } from 'vue-router';

const t = i18n.t;
const router = useRouter();

/**
 * このページで使用するStateのinterface
 */
interface SettingLogicState {
  contentState: {
    unpaidOrderedOrderAppearance: boolean | undefined;
    orderReminderAppearance: boolean | undefined;
  };
  isEdited: boolean;
}

const state = reactive<SettingLogicState>({
  contentState: {
    unpaidOrderedOrderAppearance: undefined,
    orderReminderAppearance: undefined,
  },
  isEdited: false,
});

/**
 * 設定のトグルを押した際のハンドラ
 * @param toggledKey - トグルを押した項目
 */
const onToggled = (toggledKey: string) => {
  if (!(toggledKey in myPropertyPreferences.order)) {
    return;
  }
  state.isEdited = true;
  const toggledValue = !state.contentState[toggledKey];
  state.contentState[toggledKey] = toggledValue;
};

/**
 * 保存ボタンを押した際の処理
 */
const onSave = async () => {
  state.isEdited = false;
  for (const [key, value] of Object.entries(state.contentState)) {
    if (value == null) continue;
    myPropertyPreferences.order[key] = value;
  }
  try {
    const res = await waitLoading(saveMyPropertyPreferences(), t('保存中…'), '');
    console.log(res);
    if (res == null) throw new Error();
    await openSuccessToSaveModal();
  } catch (e) {
    await openFailToSavedModal();
  }
  location.reload();
};

/**
 * ダッシュボードアラート設定ボタン押下
 */
const onClickDashboardAlertSetting = () => {
  router.push('logic/dashboard-alert');
};

onBeforeMount(async () => {
  // ページロード時にIndexedDBから現在の設定情報を取得してStateに格納
  await waitLoading(loadMyPropertyPreferences(), undefined, '');
  for (const key of Object.keys(state.contentState)) {
    if (key in myPropertyPreferences.order) {
      state.contentState[key] = myPropertyPreferences.order[key] ?? true;
    }
  }
  state.isEdited = false;
});
</script>
<template>
  <section class="setting-logic-page">
    <header class="setting-logic-page__header">
      <SettingsPageTitle :label="t('設定 > 発注・計算ロジック')" />
    </header>
    <div class="container">
      <!-- TODO: 将来的に設定項目が増えたらv-forで回したい -->
      <div class="setting-content">
        <ToggleSwitch
          :is-enabled="state.contentState['unpaidOrderedOrderAppearance']"
          :label-text="t('最新在庫の日付以前の納期を持つ未入荷の発注を検知した場合、アラートで通知する')"
          @on-toggled="() => onToggled('unpaidOrderedOrderAppearance')"
        />
        <ToggleSwitch
          :is-enabled="state.contentState['orderReminderAppearance']"
          :label-text="t('発注登録・保存後にリマインド設定を表示する')"
          @on-toggled="() => onToggled('orderReminderAppearance')"
        />
        <div class="dashboard-alert">
          {{ t('ダッシュボードのアラート設定') }}
          <TextButton button-style="btn-secondary" @click="onClickDashboardAlertSetting">{{ t('詳細設定') }}</TextButton>
        </div>
      </div>
      <div class="save">
        <span class="save__text" :class="{ invisible: !state.isEdited }">{{ t('未保存の設定変更があります') }}</span>
        <TextButton @click="onSave">{{ t('保存する') }}</TextButton>
      </div>
    </div>
  </section>
</template>
<style lang="scss" scoped>
.setting-logic-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }
}
.container {
  background-color: white;
  padding: 24px 16px 40px 18px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: auto;
}
.setting-content {
  display: grid;
  gap: 16px;
  padding-bottom: 32px;
}
.save {
  width: 100%;
  justify-self: end;
  padding-top: 32px;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid $borderDefault;
  &__text {
    font-weight: 700;
    font-size: 14px;
    color: $semanticError;
  }
}
.dashboard-alert {
  display: grid;
  font-size: 14px;
  font-weight: 700;
  grid-template-columns: 1fr max-content;
  align-items: center;
}
.invisible {
  visibility: hidden;
}
</style>
